import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import {
  CircleNotch, ArrowLeft, MagnifyingGlass, WarningCircle,
  CaretLeft, CaretRight, Database, ArrowClockwise, X, CalendarBlank,
  SortDescending, CaretUp, CaretDown, ArrowsDownUp, Columns, Check,
} from "@phosphor-icons/react";

const PAGE_SIZES = ["50", "100", "500", "all"];

function renderCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) {
    if (!v.length) return "—";
    const parts = v.map(renderCell).filter((s) => s && s !== "—");
    return parts.length ? parts.join(", ") : `${v.length} item${v.length > 1 ? "s" : ""}`;
  }
  if (typeof v === "object") {
    const parts = Object.values(v).filter((x) => x && typeof x !== "object").map(String).filter((s) => s.trim());
    return parts.length ? parts.join(" ") : "—";
  }
  return String(v);
}

function prettyCol(k) {
  return k.replace(/utc$/i, " UTC").replace(/datetime/i, "Date/Time").replace(/^./, (c) => c.toUpperCase());
}

export default function LegacyZone() {
  const { zone } = useParams();
  const [label, setLabel] = useState(zone);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("50");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [visibleCols, setVisibleCols] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const chooserRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const first = useRef(true);

  useEffect(() => {
    api.get("/legacy/zones").then((r) => {
      const z = (r.data.zones || []).find((x) => x.id === zone);
      if (z) setLabel(z.label);
    }).catch(() => {});
  }, [zone]);

  // reset when zone changes
  useEffect(() => { setPage(1); setQuery(""); setDebounced(""); setPageSize("50"); setDateFrom(""); setDateTo(""); setSortField(""); setSortDir("desc"); setVisibleCols(null); }, [zone]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  // reset to page 1 when search / page size / date range changes (but not on first mount)
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPage(1);
  }, [debounced, pageSize, dateFrom, dateTo]);

  const load = (refresh = false) => {
    setLoading(true);
    if (refresh) setRefreshing(true);
    api.get(`/legacy/zone/${zone}`, { params: { page, page_size: pageSize, search: debounced, date_from: dateFrom, date_to: dateTo, sort_field: sortField, sort_dir: sortDir, refresh } })
      .then((r) => setData(r.data))
      .catch((e) => setData({ ok: false, error: e?.response?.data?.detail || "Request failed." }))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [zone, page, pageSize, debounced, dateFrom, dateTo, sortField, sortDir]);

  // 3-state header sort: none -> asc -> desc -> none
  const toggleSort = (col) => {
    if (sortField !== col) { setSortField(col); setSortDir("asc"); }
    else if (sortDir === "asc") { setSortDir("desc"); }
    else { setSortField(""); setSortDir("desc"); }
  };

  const records = useMemo(() => data?.records || [], [data]);
  const allColumns = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r && typeof r === "object") Object.keys(r).forEach((k) => set.add(k)); });
    return Array.from(set);
  }, [records]);

  // Initialise / restore visible columns once data arrives.
  useEffect(() => {
    if (!allColumns.length || visibleCols) return;
    let init = allColumns;
    try {
      const saved = JSON.parse(localStorage.getItem(`legacy-cols-${zone}`) || "null");
      if (Array.isArray(saved) && saved.length) {
        const f = allColumns.filter((c) => saved.includes(c));
        if (f.length) init = f;
      }
    } catch (e) { console.debug("Failed to read saved column prefs", e); }
    setVisibleCols(init);
  }, [allColumns, visibleCols, zone]);

  const columns = useMemo(() => {
    if (!visibleCols) return allColumns;
    const vis = allColumns.filter((c) => visibleCols.includes(c));
    return vis.length ? vis : allColumns;
  }, [visibleCols, allColumns]);

  const persistCols = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem(`legacy-cols-${zone}`, JSON.stringify(cols)); } catch (e) { console.debug("Failed to persist column prefs", e); }
  };

  const toggleCol = (c) => {
    const cur = visibleCols || allColumns;
    if (cur.includes(c)) {
      if (cur.length <= 1) return; // keep at least one column
      persistCols(cur.filter((x) => x !== c));
    } else {
      persistCols(allColumns.filter((x) => cur.includes(x) || x === c));
    }
  };

  // close chooser on outside click
  useEffect(() => {
    const h = (e) => { if (chooserRef.current && !chooserRef.current.contains(e.target)) setChooserOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const total = data?.total || 0;
  const totalPages = data?.total_pages || 1;

  return (
    <Layout wide>
      <div data-testid="legacy-zone-page">
        <Link to="/legacy-api" data-testid="legacy-zone-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-[#0055FF]">
          <ArrowLeft size={16} weight="bold" /> Back to zones
        </Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0055FF]/10 text-[#0055FF]"><Database size={22} weight="bold" /></div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="legacy-zone-title">{label}</h1>
              <p className="font-mono text-xs text-neutral-400">zone={zone}{data?.ok ? <> · {total.toLocaleString()} records</> : null}</p>
            </div>
          </div>
          <button data-testid="legacy-zone-refresh" onClick={() => load(true)} disabled={loading}
            className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">
            <ArrowClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Filters: search + last-updated date range */}
        <div className="mt-6 flex flex-wrap items-end gap-4">
          <div className="relative w-full max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="legacy-zone-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search all ${label.toLowerCase()}…`}
              className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-10 text-base outline-none focus:border-[#0055FF]" />
            {query && (
              <button onClick={() => setQuery("")} data-testid="legacy-zone-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-[#0A0A0A]"><X size={16} /></button>
            )}
          </div>

          <div className="flex items-end gap-2 rounded-sm border border-neutral-200 bg-white px-3 py-2 shadow-sm" data-testid="legacy-date-range">
            <CalendarBlank size={18} className="mb-2 text-[#0055FF]" />
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Last updated from</label>
              <input type="date" data-testid="legacy-date-from" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)}
                className="bg-transparent text-sm text-neutral-700 outline-none" />
            </div>
            <span className="mb-1.5 text-neutral-300">→</span>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">To</label>
              <input type="date" data-testid="legacy-date-to" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)}
                className="bg-transparent text-sm text-neutral-700 outline-none" />
            </div>
            {(dateFrom || dateTo) && (
              <button data-testid="legacy-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }} className="mb-1.5 ml-1 text-neutral-400 hover:text-[#DC2626]" title="Clear date range"><X size={15} /></button>
            )}
          </div>

          {data?.ok && (sortField || data?.sort_applied) && (
            <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500" data-testid="legacy-sort-indicator">
              <SortDescending size={15} weight="bold" />
              {sortField
                ? <>Sorted by {prettyCol(sortField)} ({sortDir === "asc" ? "ascending" : "descending"})</>
                : <>Sorted by Last Updated (newest first)</>}
            </span>
          )}

          {/* Column chooser */}
          {data?.ok && allColumns.length > 0 && (
            <div className="relative mb-1 ml-auto" ref={chooserRef}>
              <button data-testid="legacy-column-chooser-btn" onClick={() => setChooserOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 shadow-sm hover:bg-neutral-50">
                <Columns size={16} weight="bold" /> Columns
                <span className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500">{columns.length}/{allColumns.length}</span>
                <CaretDown size={13} weight="bold" className={`transition-transform ${chooserOpen ? "rotate-180" : ""}`} />
              </button>
              {chooserOpen && (
                <div className="absolute right-0 z-30 mt-1 w-64 rounded-sm border border-neutral-200 bg-white p-2 shadow-lg" data-testid="legacy-column-chooser-panel">
                  <div className="mb-1 flex items-center justify-between px-1 pb-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Show columns</span>
                    <button data-testid="legacy-columns-showall" onClick={() => persistCols(allColumns)} className="text-xs font-semibold text-[#0055FF] hover:underline">Show all</button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {allColumns.map((c) => {
                      const on = (visibleCols || allColumns).includes(c);
                      return (
                        <button key={c} data-testid={`legacy-col-toggle-${c}`} onClick={() => toggleCol(c)}
                          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50">
                          <span className={`flex h-4 w-4 items-center justify-center rounded-[3px] border ${on ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300"}`}>
                            {on && <Check size={11} weight="bold" />}
                          </span>
                          <span className="truncate">{prettyCol(c)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {data?.truncated && (
          <p className="mt-2 text-xs text-[#D97706]">Showing the first {total.toLocaleString()} records (large zone — capped for performance).</p>
        )}

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-sm">
          {loading && !data ? (
            <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
          ) : data?.ok && records.length ? (
            <div className="relative overflow-x-auto">
              {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60"><CircleNotch size={24} className="animate-spin text-[#0055FF]" /></div>}
              <table className="w-full min-w-full text-left text-sm" data-testid="legacy-zone-table">
                <thead className="sticky top-0">
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    {columns.map((c) => {
                      const active = sortField === c;
                      return (
                        <th key={c} className="whitespace-nowrap px-4 py-3">
                          <button
                            data-testid={`legacy-col-${c}`}
                            onClick={() => toggleSort(c)}
                            className={`group inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${active ? "text-[#0055FF]" : "text-neutral-500 hover:text-[#0A0A0A]"}`}
                          >
                            {prettyCol(c)}
                            {active ? (
                              sortDir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
                            ) : (
                              <ArrowsDownUp size={12} weight="bold" className="opacity-0 transition-opacity group-hover:opacity-50" />
                            )}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i} className={`border-b border-neutral-100 last:border-0 transition-colors hover:bg-[#0055FF]/[0.03] ${i % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`legacy-zone-row-${i}`}>
                      {columns.map((c) => {
                        const text = renderCell(r?.[c]);
                        return (
                          <td key={c} className="max-w-[280px] truncate px-4 py-2.5 text-[13px] text-neutral-700" title={text}>{text}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : data?.ok && !records.length ? (
            <div className="px-4 py-16 text-center" data-testid="legacy-zone-empty">
              <p className="text-sm text-neutral-500">{debounced ? `No records match “${debounced}”.` : "No records in this zone."}</p>
            </div>
          ) : (
            <div className="p-6" data-testid="legacy-zone-error">
              <div className="flex items-center gap-2 text-[#D97706]">
                <WarningCircle size={18} weight="fill" />
                <span className="font-semibold">{data?.auth_failed ? "Authentication failed" : "Couldn't load data"}</span>
              </div>
              <p className="mt-1 text-sm text-neutral-600">{data?.status_message || data?.error || "The legacy API did not return any records."}</p>
              {data?.auth_failed && <p className="mt-2 text-sm text-neutral-600">Check the <Link to="/legacy-api" className="font-medium text-[#0055FF] hover:underline">API credentials</Link>.</p>}
            </div>
          )}

          {/* Totals row */}
          {data?.ok && (
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 bg-neutral-50 px-4 py-3" data-testid="legacy-zone-totals">
              <p className="text-sm text-neutral-600" data-testid="legacy-zone-showing">
                {total ? <>Showing <span className="font-semibold text-[#0A0A0A]">{data.showing_from?.toLocaleString()}–{data.showing_to?.toLocaleString()}</span> of <span className="font-semibold text-[#0A0A0A]">{total.toLocaleString()}</span></> : "No records"}
              </p>

              <div className="flex items-center gap-2 text-sm text-neutral-600">
                <span>Rows per page</span>
                <select data-testid="legacy-zone-pagesize" value={pageSize} onChange={(e) => setPageSize(e.target.value)}
                  className="rounded-sm border border-neutral-300 bg-white px-2.5 py-1.5 font-semibold text-[#0A0A0A] outline-none focus:border-[#0055FF]">
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-neutral-600" data-testid="legacy-zone-pagecount">
                  Page <span className="font-semibold text-[#0A0A0A]">{data.page}</span> of <span className="font-semibold text-[#0A0A0A]">{totalPages}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button data-testid="legacy-zone-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1 || pageSize === "all"}
                    className="flex h-8 w-8 items-center justify-center rounded-sm border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40">
                    <CaretLeft size={15} weight="bold" />
                  </button>
                  <button data-testid="legacy-zone-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages || pageSize === "all"}
                    className="flex h-8 w-8 items-center justify-center rounded-sm border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40">
                    <CaretRight size={15} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
